import * as React from "react";
import {
  BorrowerField,
  BorrowerFieldInputType,
  ObrFormFieldUsage,
} from "../../OBRSettings";
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useLanguage } from "../../Language";
import { FormTextField } from "@tlc/forms/FormTextField";

export interface BorrowerFieldProps<T extends FieldValues> {
  configuration: BorrowerField;
}

// Dynamic borrower fields
export const BorrowerFieldControl = <T extends FieldValues>({
  configuration,
}: BorrowerFieldProps<T>) => {
  const { control, formState, getFieldState } = useFormContext<T>();

  // TODO pass in as a name prop instead
  const fieldName = `fields.${configuration.fieldNumber}` as Path<T>;
  const { error } = getFieldState(fieldName, formState);

  const { translate } = useLanguage();

  if (configuration.obrUsage === ObrFormFieldUsage.Hidden) {
    return null;
  }

  const isRequired = configuration.obrUsage === ObrFormFieldUsage.Required;

  return (
    <>
      {configuration.inputType === BorrowerFieldInputType.DropDown && (
        <Controller<T>
          name={fieldName as Path<T>}
          control={control}
          rules={{
            required: isRequired
              ? `${translate("isRequired", {
                  LABEL: configuration.description,
                })}`
              : false,
          }}
          render={({ field: { ref, ...field } }) => (
            <FormControl
              sx={{ maxWidth: "320px" }}
              error={!!error}
              required={isRequired}
            >
              <InputLabel id={`${field.name}-label`}>
                {configuration.description}
              </InputLabel>
              <Select
                labelId={`${field.name}-label`}
                {...field}
                inputProps={{ ref: ref }}
                label={configuration.description}
              >
                {configuration.dropDownOptions.map((option) => (
                  <MenuItem key={option.userValue} value={option.userValue}>
                    {option.description}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{error?.message || " "}</FormHelperText>
            </FormControl>
          )}
        />
      )}
      {configuration.inputType === BorrowerFieldInputType.TextBox && (
        <FormTextField<T>
          label={configuration.description}
          required={isRequired}
          name={fieldName}
          maxLength={250}
        />
      )}
    </>
  );
};
